import React, { useContext, useMemo, useState } from 'react'

import { useSelector } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { Dropdown, Menu } from 'antd'
import Search from 'antd/lib/input/Search'
import { faCalendar } from '@fortawesome/pro-regular-svg-icons'
import { faFileExport, faFilter, faPlus } from '@fortawesome/pro-solid-svg-icons'

import { selectUser } from '../../../reducers/UserReducer'
import InternshipsManagerActionsHeaderExportModal from '../InternshipsManagerActionsHeaderExportModal'
import { getLanguageByLocale, isObject } from '../../../utils'
import { onError } from '../../../utils/apiHelper'
import PaginationHeader from './PaginationHeader'
import WeekNavigator from './WeekNavigator'
import CurrentWeekButton from './CurrentWeekButton'
import { DATE_FORMAT_SHOWN } from './utils/constants'
import { GlobalContext } from '../../../Providers/GlobalProvider'
import { Routes } from '../../../routes'
import ImportInternshipsButton from './ImportInternshipsButton'
import { GlobalFiltersContext } from '../../../Providers/GlobalFiltersProvider'
import InternshipFilters, { getActiveFiltersCount } from '../../../Views/institution/InternshipsView/InternshipsList/InternshipFilters'
import { isCoordinator, isDomainEvaluator, isEncoder, isValidator, isHospitalAdmin } from '../../../utils/roles'
import IconButton from '../../antd/Buttons/IconButton'
import TooltipIconButton from '../../antd/Buttons/TooltipIconButton'
import AutonomousInternshipFormModal, { AutonomousInternshipFormModalContext, Modes } from '../../../Providers/Forms/InternshipForm/AutonomousInternshipFormModal'
import { Internship, InternshipProperties } from '../../../utils/entities/internship'
import FormButton from '../../antd/Buttons/FormButton'
import { exportTimeline } from '../../../utils/api/internship'
import { getDocument } from '../../../utils/api/export'
import DraftButton from '../DraftButton'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import useModules from '../../../hooks/useModules'
import { InstitutionContext } from '../../../Providers/InstitutionProvider'
import ResetButton from '../../antd/Buttons/ResetButton'

import '../../../assets/data-table.scss'
import './assets/internships-manager.scss'

export const PRECISION_YEAR = 'year'
const PRECISION_SCHOOL = 'school'
const PRECISION_SECTION = 'section'
const PRECISION_SECTOR = 'sector'
const PRECISION_OPTIONS = [
  { value: PRECISION_SECTOR, label: 'Quotas per sector' },
  { value: PRECISION_SECTION, label: 'Quotas per section' },
  { value: PRECISION_SCHOOL, label: 'Quotas per school' },
  { value: PRECISION_YEAR, label: 'Default quotas' }
]

const KEYS_TO_VALIDATE = [
  InternshipProperties.SCHOOL,
  InternshipProperties.START_DATE,
  InternshipProperties.END_DATE,
  InternshipProperties.SECTION,
  InternshipProperties.SECTOR,
  InternshipProperties.INSTITUTION
]

const SEARCH_FIELD_STYLE = { width: 175 }

const InternshipsHeader = ({
  addMode, history, loading, metadata, page, precision,
  onFilterChange, onPageChange, onPrecisionChange, onResetFilters, toggleAddMode, refresh
}) => {
  const { setCurrentDate } = useContext(GlobalContext)
  const { internshipDate, internshipFilters, internshipSearch, setInternshipDate, setInternshipSearch } = useContext(GlobalFiltersContext)
  const ic = useContext(InstitutionContext)
  const { institutions, order, schools, sections, states, weeksShownCount } = internshipFilters

  const ordonnedSectors = isObject(ic) ? ic.sectorsOrderByName : []

  const modules = useModules()

  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [exportLoading, setExportLoading] = useState(false)
  const [displayExportModal, setDisplayExportModal] = useState(false)

  const buttonsConfig = useMemo(() => {
    const shouldDisplayAddButton = isEncoder(user) || isCoordinator(user)
    let scheduleButtonLabel = 'internships_header.schedule_button.edit'

    if (isValidator(user)) {
      scheduleButtonLabel = 'internships_header.schedule_button.validate'
    } else if (isDomainEvaluator(user)) {
      scheduleButtonLabel = 'internships_header.schedule_button.see'
    }

    return { shouldDisplayAddButton, scheduleButtonLabel }
  }, [user])

  const precisionOptions = useMemo(() => PRECISION_OPTIONS.map(o => ({ ...o, label: t(o.label) })), [t])

  const handleExportTimeline = (startDate, endDate, checked, filter) => {
    setDisplayExportModal(false)
    setExportLoading(true)

    const body = {
      start: startDate.format('YYYY-MM-DD'),
      end: endDate.format('YYYY-MM-DD'),
      language: getLanguageByLocale(user.language)
    }

    if (checked) {
      Object.keys(filter).forEach(key => {
        if (filter[key]) {
          if (typeof body.filter === 'undefined') {
            body.filter = {}
          }

          body.filter[key] = filter[key]
        }
      })
    }

    exportTimeline(user, body).then(json => {
      getDocument(
        json.job,
        user,
        () => onError(t('spreadsheet_download.error'))
      )
    }).catch(error => {
      console.error(error.message)
      onError(t('internships_export.error'))
    })
  }

  const resetFilters = useMemo(() => {
    let activeFiltersCount = getActiveFiltersCount(internshipFilters)

    if (internshipSearch.trim().length !== 0) {
      activeFiltersCount += 1
    }

    return activeFiltersCount
  }, [getActiveFiltersCount, internshipFilters, internshipSearch, loading])

  return (
    <div className='internships-manager-header'>
      <div className='actions-and-filters'>
        <Search
          value={internshipSearch}
          placeholder={t('Filter internships')}
          onChange={e => { setInternshipSearch(e.target.value) }}
          style={SEARCH_FIELD_STYLE}
          className='app-tour-2'
        />

        <div className='h-spacing' />

        <Dropdown
          overlay={
            <Menu>
              <Menu.ItemGroup title={t('Filters')}>
                <Menu.Item disabled style={{ cursor: 'default' }}>
                  <InternshipFilters
                    internshipFilters={internshipFilters}
                    precision={precision}
                    precisionOptions={precisionOptions}
                    user={user}
                    onPrecisionChange={onPrecisionChange}
                    onFilterChange={onFilterChange}
                  />
                </Menu.Item>
              </Menu.ItemGroup>
            </Menu>
          }
          overlayClassName='custom-overlay'
          trigger={['click']}
        >
          <IconButton icon={faFilter} />
        </Dropdown>

        <div className='h-spacing' />
        <ResetButton
          disabled={!resetFilters}
          onClick={onResetFilters}
        />

        {modules.internshipsDraft && <DraftButton />}

        <div className='flex-fill' />
        <IconButton
          type='primary'
          icon={faCalendar}
          disabled={loading}
          ghost
          onClick={() => {
            setCurrentDate(internshipDate)
            history.push(Routes.SHIFTS_MANAGER)
          }}
          text={t(buttonsConfig.scheduleButtonLabel)}
        />
        {buttonsConfig.shouldDisplayAddButton && (
          <>
            <div className='h-spacing' />
            <TooltipIconButton
              title={t(`internships_header.add_internship_button.tooltip.${addMode ? 'hide' : 'show'}`)}
              text={t('internships_header.add_internship_button.label')}
              onClick={toggleAddMode}
              icon={faPlus}
              tooltipProps={{ placement: 'top' }}
              buttonProps={{
                type: 'primary',
                className: 'app-tour-1',
                disabled: loading,
                ghost: true
              }}
            />
          </>
        )}
        {isHospitalAdmin(user) && (
          <>
            <div className='h-spacing' />
            <AutonomousInternshipFormModal keysToValidate={KEYS_TO_VALIDATE} sectors={ordonnedSectors}>
              <FormButton
                context={AutonomousInternshipFormModalContext}
                icon={faPlus}
                text={t('internships_header.add_internship_button.label')}
                type='primary'
                disabled={loading}
                onClick={setter => setter({ object: new Internship({ institution: user.institutions[0] }), callback: refresh, mode: Modes.CREATE })}
                ghost
              />
            </AutonomousInternshipFormModal>
          </>
        )}
      </div>
      <div className='table-modifier-buttons'>
        <PaginationHeader
          pageIndex={page}
          maxPageIndex={metadata.totalPages}
          dataType='Internship'
          dataCount={metadata.total}
          onPageChange={onPageChange}
        />
        <div className='flex-fill' />
        <WeekNavigator
          loading={loading}
          date={internshipDate}
          dateFormat={DATE_FORMAT_SHOWN}
          onDateChange={setInternshipDate}
        />
        <div className='h-spacing' />
        <CurrentWeekButton
          date={internshipDate}
          loading={loading}
          title={t('Go back to current week')}
          onClick={setInternshipDate}
        />
        {isEncoder(user) && (
          <>
            <div className='h-spacing' />
            <ImportInternshipsButton
              afterUpload={() => setInternshipDate(internshipDate.clone())}
              school={user.school?.id ?? -1}
            />
          </>
        )}
        <div className='h-spacing' />
        <TooltipIconButton
          title={t('internships_header.export_timeline_button')}
          icon={faFileExport}
          onClick={() => setDisplayExportModal(true)}
          tooltipProps={{ placement: 'top' }}
          buttonProps={{ loading: exportLoading }}
        />
      </div>
      {displayExportModal && (
        <InternshipsManagerActionsHeaderExportModal
          startDate={internshipDate}
          weeksShownCount={weeksShownCount}
          filterSearch={internshipSearch}
          filterStates={states}
          filterSchools={schools}
          filterSections={sections}
          filterInstitutions={institutions}
          orderBy={order.by}
          orderDirection={order.direction}
          onCancel={() => setDisplayExportModal(false)}
          onExport={handleExportTimeline}
          visible
        />
      )}
    </div>
  )
}

export default withRouter(InternshipsHeader)
